import React from 'react'
import Layout from '~/components/Layout';
import { useLang } from '~/hooks/useLang';
import InternalHeader from '~/components/Layout/components/InternalHeader';
import InternalContent from '~/components/Layout/components/InternalContent';
import { Button, Divider, Grid, Paper, Step, StepContent, StepLabel, Stepper, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useWorkScheduleForm } from '../hooks/useWorkScheduleForm';
import AssociateShiftAndDate from '../components/AssociateShiftAndDate';
import AssociateDevice from '../components/AssociateDevice';
import AssociateStaff from '../components/AssociateStaff';
import StepActions from '../components/StepActions';
import type { WorkScheduleFormSteps } from '../interfaces';

export default function Create() {
  const { translate } = useLang();
  const {
    startDate,
    endDate,
    initialPeriodCreated,
    workShift,
    device,
    crewPositionId,
    crewList,
    task,
    userId,
    resetValues,
    focusChange,
    crewPositionOptions,
    activeStep,
    disabledNextButton,
    checkAvailabilityResult,
    // functions
    handleCancelButton,
    onPeriodChangeCreated,
    addCrewMember,
    defineEndDateRestHours,
    resetForm,
    handleCreateAndEditWorkSchedule,
    handleNext,
    handleBack,
    handleReset,
  } = useWorkScheduleForm();

  const steps: ReadonlyArray<WorkScheduleFormSteps> = [
    {
      id: 0,
      label:
        <Typography variant='h6'>
          {translate('Associate Shift and Date')}
        </Typography>,
      content: <AssociateShiftAndDate
        startDate={startDate}
        endDate={endDate}
        workShift={workShift}
        initialPeriodCreated={initialPeriodCreated}
        onPeriodChangeCreated={onPeriodChangeCreated}
        defineEndDateRestHours={defineEndDateRestHours}
        disabledFields={activeStep.get() !== 0}
      />,
    }, {
      id: 1,
      label:
        <Typography variant='h6'>
          {translate('Associate Device')}
        </Typography>
      ,
      content: <AssociateDevice
        device={device}
        task={task}
        resetValues={resetValues}
        focusChange={focusChange}
        endDate={endDate}
        disabledNextButton={disabledNextButton}
        checkAvailabilityResult={checkAvailabilityResult}
        activeStep={activeStep}
        disabledFields={activeStep.get() !== 1}
      />,
    }, {
      id: 2,
      label:
        <Typography variant='h6'>
          {translate('Associate Staff')}
        </Typography>,
      content: <AssociateStaff
        crewPositionId={crewPositionId}
        focusChange={focusChange}
        resetValues={resetValues}
        userId={userId}
        device={device}
        crewPositionOptions={crewPositionOptions.get()}
        onAddCrewMember={addCrewMember}
        crewList={crewList}
        activeStep={activeStep}
        checkAvailabilityResult={checkAvailabilityResult}
        disabledFields={activeStep.get() !== 2}
      />,
    }
  ]
  const finalStep = activeStep.get() === steps.length;

  return (
    <Layout marginLayout={true}>
      <InternalHeader title={translate('Create New work schedule')} />
      <InternalContent expandButton={false} >
        <Stepper activeStep={activeStep.get()} orientation='vertical'>
          {steps.map((step, index) => {
            return (
              <Step key={step.id} expanded={finalStep ? false : activeStep.get() >= index ? true : false}>
                <StepLabel>
                  {step.label}
                </StepLabel>
                <StepContent TransitionProps={{ unmountOnExit: false }}>
                  {step.content}
                  {activeStep.get() === index && (
                    <StepActions
                      handleNext={handleNext}
                      handleBack={handleBack}
                      index={index}
                      steps={steps}
                      disabledNextButton={disabledNextButton}
                    />
                  )}
                </StepContent>
              </Step>
            )
          })}
        </Stepper>
        {finalStep && (
          <>
            <Typography>
              {translate("All steps completed - you're finished")}
            </Typography>
            <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
              {translate('Reset')}
            </Button>
          </>
        )}
        <Grid container xs={12} justifyContent="flex-end" spacing={2} sx={{ mt: 2 }}>
          <Grid item>
            <Button
              variant='outlined'
              onClick={() => handleCancelButton()}
            >
              {translate('Cancel')}
            </Button>
          </Grid>
          <Grid item>
            <LoadingButton
              variant='contained'
              disabled={activeStep.get() !== steps.length}
              onClick={() => handleCreateAndEditWorkSchedule({ isCreate: true })}
            >
              {translate('Save')}
            </LoadingButton>
          </Grid>
        </Grid>

      </InternalContent>
    </Layout >
  )
}